import { useCallback, useEffect, useMemo, useState } from 'react';

import useMobxActions from '@src/hooks/useMobxActions';
import useMobxSelector from '@src/hooks/useMobxSelector';
import { getCategoryFirstPid } from '@src/utils/helpCenter';

import { PlatformCategory, useSupportHomeSearch, useSupportSearchPagination } from './hooks';
import SupportHome from './widgets/supportHome';
import SupportSearch from './widgets/supportSearch';

import './index.scss';

const Support = () => {
  const [showSearch, setShowSearch] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('allResults') === 'true';
  });
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const actions = useMobxActions();

  // 共享的 keyword 状态
  const [keyword, setKeyword] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('keyword') || '';
  });

  // 分类筛选状态
  const [selectedCategory, setSelectedCategory] = useState<PlatformCategory>('all');

  // SupportHome 搜索 hook - 只加载前 10 个
  const homeSearch = useSupportHomeSearch(showGlobalSearch ? keyword : '');

  // SupportSearch 分页搜索 hook - 支持分页加载
  const paginationSearch = useSupportSearchPagination(showSearch ? keyword : '');

  // 从store获取数据
  const catalogsByPlatform = useMobxSelector((stores) => stores.helpCenterStore.catalogsByPlatform);

  useEffect(() => {
    actions.helpCenterStore.fetchCatalogs('publisher');
    actions.helpCenterStore.fetchCatalogs('advertiser');
    actions.helpCenterStore.fetchPopularPosts();
  }, [actions]);

  // 生成搜索建议 - 使用两个平台的一级目录
  const searchSuggestions = useMemo(() => {
    const publisherCatalogs = catalogsByPlatform.publisher || [];
    const advertiserCatalogs = catalogsByPlatform.advertiser || [];

    const groups = [];

    // User Growth 平台建议
    if (advertiserCatalogs.length > 0) {
      groups.push({
        title: 'User Growth',
        suggestions: advertiserCatalogs.map((catalog, index) => {
          return {
            id: `advertiser_${catalog.id || index}`,
            label: catalog.name || 'Untitled',
            url: `/supportcenter?pid=${getCategoryFirstPid(catalog)}&platform=advertiser`,
          };
        }),
      });
    }

    // Monetization 平台建议
    if (publisherCatalogs.length > 0) {
      groups.push({
        title: 'Monetization',
        suggestions: publisherCatalogs.map((catalog, index) => ({
          id: `publisher_${catalog.id || index}`,
          label: catalog.name || 'Untitled',
          url: `/supportcenter?pid=${getCategoryFirstPid(catalog)}&platform=publisher`,
        })),
      });
    }

    return groups;
  }, [catalogsByPlatform]);

  // 处理 keyword 变化
  const handleSetKeyword = useCallback((newKeyword: string) => {
    setKeyword(newKeyword);
  }, []);

  // 处理分类变化
  const handleSetSelectedCategory = useCallback((category: PlatformCategory) => {
    setSelectedCategory(category);
  }, []);

  /**
   * onViewMore 点击查看全部，跳转到搜索页面
   */
  const onViewMore = useCallback(() => {
    setShowSearch(true);
    setShowGlobalSearch(false);
  }, []);

  /**
   * onBack 从搜索结果返回支持首页
   */
  const onBack = useCallback(() => {
    setShowSearch(false);
    setShowGlobalSearch(false);
    setKeyword('');
    setSelectedCategory('all');
    homeSearch.reset();
    paginationSearch.reset();
  }, [homeSearch, paginationSearch]);

  return (
    <>
      {showSearch ? (
        <SupportSearch
          onBack={onBack}
          keyword={keyword}
          selectedCategory={selectedCategory}
          searchState={paginationSearch.state}
          onKeywordChange={handleSetKeyword}
          onCategoryChange={handleSetSelectedCategory}
          onLoadMore={paginationSearch.loadMore}
        />
      ) : (
        <SupportHome
          onViewMore={onViewMore}
          searchSuggestions={searchSuggestions}
          keyword={keyword}
          searchState={homeSearch.state}
          onKeywordChange={handleSetKeyword}
          showGlobalSearch={showGlobalSearch}
          setShowGlobalSearch={setShowGlobalSearch}
        />
      )}
    </>
  );
};

export default Support;
